import React, { useState } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { XMarkIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { QuestionMarkCircleIcon } from '@heroicons/react/20/solid'

const faqs = [
  {
    question: "Can I return or get a refund for my order?",
    answer: "Please only order what you require, as Integria has a strict no-returns/refunds policy.",
  },
  {
    question: "How long is my prescription valid for?",
    answer: "This prescription is valid for 6 months or until all products have been purchased.",
  },
  {
    question: "Can I order part of my prescription?",
    answer: "You may order all or part of this prescription, and the remaining products can be ordered at a later date (within 6 months of issue).",
  },
  {
    question: "Who should I contact for more information on these products?",
    answer: "For more information on these products, please consult your prescribing health practitioner.",
  },
  {
    question: "When I press the pay button, nothing happens?",
    answer: "This is most likely an address error. Please ensure the address is selected from the drop-down provided and not typed out manually. Please note special characters are not valid in the address lines.",
  },
  {
    question: "Can I ship/Bill to PO Boxes?",
    answer: "No, PO boxes are not accepted in the 'ship to' or 'bill to' address lines on the MyPatient ordering site.",
  },
  {
    question: "If a product is out of stock, will I be notified when it is available again?",
    answer: "Yes, for any out of stock products on your script, we will send you an email to advise when this product is back in stock. If you wish to stop receiving these notifications, please contact your Practitioner to delete the script.",
  },
  {
    question: "What is the freight cost and freight free threshold?",
    answer: "Freight cost is $8.50 + Tax unless an order is valued at $200 or more + Tax, in which case you will receive free shipping.",
  },
  {
    question: "Will Custom Herbal Tinctures arrive at the same time as the rest of the products ordered?",
    answer: "Custom herbal orders are shipped independently and will not be in the same box as the rest of the items ordered.",
  },
  {
    question: "Who do I contact regarding script issues?",
    answer: "Any issues with the script, including refills and merging multiple scripts, will need to go directly to your practitioner.",
  },
]

export const FaqPanel = () => {
  return (
    <div className="mx-auto max-w-4xl divide-y divide-gray-900/10">
      <h2 className="text-2xl font-bold leading-10 tracking-tight text-gray-900">Frequently asked questions</h2>
      <dl className="mt-10 space-y-6 divide-y divide-gray-900/10">
        {faqs.map((faq) => (
          <Disclosure as="div" key={faq.question} className="pt-6">
            {({ open }) => (
              <>
                <dt>
                  <DisclosureButton className="flex w-full items-start justify-between text-left text-gray-900">
                    <span className="text-base font-semibold leading-7">{faq.question}</span>
                    <span className="ml-6 flex h-7 items-center">
                      {open ? (
                        <MinusIcon className="h-6 w-6" aria-hidden="true" />
                      ) : (
                        <PlusIcon className="h-6 w-6" aria-hidden="true" />
                      )}
                    </span>
                  </DisclosureButton>
                </dt>
                <DisclosurePanel as="dd" className="mt-2 pr-12">
                  <p className="text-base leading-7 text-gray-600">{faq.answer}</p>
                </DisclosurePanel>
              </>
            )}
          </Disclosure>
        ))}
      </dl>
    </div>  
  )
}

export default function FaqModal() {
  const [open, setOpen] = useState(false)

  return (
    <>
      <div onClick={() => setOpen(true)} className='group inline-flex text-sm text-gray-500 hover:text-gray-700 cursor-pointer'>
        <span>
          Frequently asked questions
        </span>
        <QuestionMarkCircleIcon className='ml-2 h-5 w-5 shrink-0 text-gray-400 group-hover:text-gray-500' />
      </div>
      <Dialog className="relative z-10" open={open} onClose={setOpen}>
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        /> 
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-screen-md sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div>
                <div className="absolute right-0 top-0 block pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => setOpen(false)}
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <FaqPanel />
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  )
}
